/**
 * Location Normalization Utilities
 * 
 * Extracts clean country names from raw location strings for filter display.
 * Handles various formats: full addresses, city+country, country-only, abbreviations.
 */

import { INVESTOR_COUNTRIES } from '../constants/countries';
import * as investorCountryMappings from '../../functions/src/shared/investorCountryMappings.js';

const INVESTOR_COUNTRY_SET = new Set<string>(INVESTOR_COUNTRIES);
const BASE_COUNTRY_MAPPINGS = investorCountryMappings.COUNTRY_MAPPINGS as Record<
  string,
  string
>;

/** Filter/location extras only — never used by CSV import. */
const FILTER_ONLY_ALIASES: Record<string, string> = {
  // US States (map to United States)
  'california': 'United States',
  'new york': 'United States',
  'texas': 'United States',
  'florida': 'United States',
  'delaware': 'United States',
  'massachusetts': 'United States',
  'washington': 'United States',
  'colorado': 'United States',
  'illinois': 'United States',
  'georgia': 'United States',
  'north carolina': 'United States',
  'virginia': 'United States',
  'pennsylvania': 'United States',
  'ohio': 'United States',
  'arizona': 'United States',
  'nevada': 'United States',
  'oregon': 'United States',
  'utah': 'United States',
  'connecticut': 'United States',
  'new jersey': 'United States',
  'maryland': 'United States',
  
  // Australian states (map to Australia)
  'nsw': 'Australia',
  'new south wales': 'Australia',
  'victoria': 'Australia',
  'queensland': 'Australia',
  'western australia': 'Australia',
  'south australia': 'Australia',
  'tasmania': 'Australia',
  
  // Canadian provinces (map to Canada)
  'ontario': 'Canada',
  'british columbia': 'Canada',
  'quebec': 'Canada',
  'alberta': 'Canada',
};

const COUNTRY_MAPPINGS: Record<string, string> = {
  ...BASE_COUNTRY_MAPPINGS,
  ...FILTER_ONLY_ALIASES,
};

// List of known countries to search for in strings
const KNOWN_COUNTRIES = [
  'United States',
  'United Kingdom',
  'Australia',
  'Canada',
  'New Zealand',
  'Hong Kong',
  'Singapore',
  'Germany',
  'France',
  'Netherlands',
  'Ireland',
  'Switzerland',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Israel',
  'Japan',
  'South Korea',
  'China',
  'India',
  'Brazil',
  'Mexico',
  'Spain',
  'Italy',
  'Portugal',
  'Belgium',
  'Austria',
  'Poland',
  'Czech Republic',
  'United Arab Emirates',
  'South Africa',
  'Argentina',
  'Chile',
  'Colombia',
  'Indonesia',
  'Malaysia',
  'Thailand',
  'Vietnam',
  'Philippines',
  'Taiwan',
  'Russia',
  'Ukraine',
  'Romania',
  'Hungary',
  'Greece',
  'Turkey',
  'Egypt',
  'Nigeria',
  'Kenya',
  'Saudi Arabia',
  'Qatar',
  'Kuwait',
  'Bahrain',
  'Luxembourg',
  'Monaco',
  'Liechtenstein',
  'Estonia',
  'Latvia',
  'Lithuania',
  'Slovenia',
  'Croatia',
  'Serbia',
  'Bulgaria',
  'Iceland',
];

/** ISO-2 / short labels for constrained UI (summary bar, table cells). Keys are lowercase. */
const COUNTRY_DISPLAY_ABBREVIATIONS: Record<string, string> = {
  'united kingdom': 'UK',
  uk: 'UK',
  'great britain': 'UK',
  england: 'UK',
  'united states': 'US',
  usa: 'US',
  us: 'US',
  'united states of america': 'US',
  'united arab emirates': 'UAE',
  uae: 'UAE',
  germany: 'DE',
  deutschland: 'DE',
  india: 'IN',
  australia: 'AU',
  au: 'AU',
  'new zealand': 'NZ',
  nz: 'NZ',
  france: 'FR',
  canada: 'CA',
  singapore: 'SG',
  sg: 'SG',
  'hong kong': 'HK',
  hk: 'HK',
  switzerland: 'CH',
  netherlands: 'NL',
};

/**
 * Abbreviate a country or location label for display in tight UI.
 * Persists full names in data; use title/tooltip for the original value.
 */
export function formatCountryForDisplay(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') {
    return '';
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return '';
  }
  const normalized = trimmed.toLowerCase();
  return COUNTRY_DISPLAY_ABBREVIATIONS[normalized] ?? trimmed;
}

/**
 * Normalise a raw location string to a canonical INVESTOR_COUNTRIES name.
 * Returns null when the value is empty, ambiguous, or not a recognised single country.
 * Used by migration scripts and sync paths — does not substring-guess region strings.
 */
export function normaliseToInvestorCountry(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.toLowerCase();
  const mapped = COUNTRY_MAPPINGS[normalized];
  if (mapped && INVESTOR_COUNTRY_SET.has(mapped)) {
    return mapped;
  }

  if (INVESTOR_COUNTRY_SET.has(trimmed)) {
    return trimmed;
  }

  return null;
}

/**
 * Extract country from a raw location string.
 * Handles addresses, city+country formats, and abbreviations.
 * 
 * @param raw - Raw location string from questionnaire
 * @returns Normalized country name or 'Other' if unknown
 */
export function extractCountryFromLocation(raw: string): string {
  if (!raw || typeof raw !== 'string') {
    return 'Unknown';
  }
  
  const trimmed = raw.trim();
  if (!trimmed) {
    return 'Unknown';
  }
  
  const normalized = trimmed.toLowerCase();
  
  // Check direct mapping first (exact match)
  if (COUNTRY_MAPPINGS[normalized]) {
    return COUNTRY_MAPPINGS[normalized];
  }
  
  // Check if any known country appears in the string
  for (const country of KNOWN_COUNTRIES) {
    if (normalized.includes(country.toLowerCase())) {
      return country;
    }
  }
  
  // Check mappings as substrings (e.g., "Hong Kong" in address).
  // Skip 2–3 letter ISO codes so "born in London" does not become India.
  for (const [key, value] of Object.entries(COUNTRY_MAPPINGS)) {
    if (key.length < 4) continue;
    if (normalized.includes(key)) {
      return value;
    }
  }
  
  // Last resort: try to extract last part after comma (often country)
  const parts = trimmed.split(',').map(p => p.trim());
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1].toLowerCase();
    if (COUNTRY_MAPPINGS[lastPart]) {
      return COUNTRY_MAPPINGS[lastPart];
    }
    // Check if last part is a known country
    for (const country of KNOWN_COUNTRIES) {
      if (lastPart === country.toLowerCase()) {
        return country;
      }
    }
  }
  
  // Return 'Other' for unrecognized locations
  return 'Other';
}

/**
 * Normalize an array of raw locations to unique country names.
 * Useful for building filter options from questionnaire data.
 * 
 * @param rawLocations - Array of raw location strings
 * @returns Sorted array of unique country names
 */
export function normalizeLocationsToCountries(rawLocations: string[]): string[] {
  const countries = new Set<string>();
  
  for (const raw of rawLocations) {
    const country = extractCountryFromLocation(raw);
    if (country !== 'Unknown') {
      countries.add(country);
    }
  }
  
  // Sort alphabetically, but put 'Other' at the end
  const sorted = Array.from(countries).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
  
  return sorted;
}

/**
 * Create a mapping of normalized countries to original raw values.
 * Useful when you need to filter by country but match against raw data.
 * 
 * @param rawLocations - Array of raw location strings
 * @returns Map of country -> Set of raw values that map to that country
 */
export function createCountryToRawMapping(rawLocations: string[]): Map<string, Set<string>> {
  const mapping = new Map<string, Set<string>>();
  
  for (const raw of rawLocations) {
    const country = extractCountryFromLocation(raw);
    if (country !== 'Unknown') {
      if (!mapping.has(country)) {
        mapping.set(country, new Set());
      }
      mapping.get(country)!.add(raw);
    }
  }
  
  return mapping;
}